import { UseFormResetField } from 'react-hook-form';

import {
  GroupedSelectGroup,
  GroupedSelectOption,
} from '@/components/Form/GroupedSelect';

import {
  NoTaxNumberReasonCategory,
  NoTaxNumberReasonCode,
} from '@/types/api/tax-info';
import { TranslatorFunction } from '@/types/i18n';

import { OtherCountriesTaxFormValues } from '../utils/types';
import {
  FOREIGN_NO_TAX_NUMBER_REASON_CATEGORY_CODES,
  NO_TAX_NUMBER_REASON_CODE_TO_CATEGORY,
  NOT_LEGALLY_REQUIRED_REASON_CODES,
  UNABLE_TO_PROVIDE_NOW_REASON_CODES,
} from './constants';

export function isNoTaxNumberReasonCategory(
  value?: string,
): value is NoTaxNumberReasonCategory {
  return FOREIGN_NO_TAX_NUMBER_REASON_CATEGORY_CODES.some(
    category => category === value,
  );
}

export function isNoTaxNumberReasonCode(
  value?: string,
): value is NoTaxNumberReasonCode {
  return Object.values(NoTaxNumberReasonCode).some(code => code === value);
}

export function getNoTaxNumberReasonCategoryForCode(
  code?: string,
): NoTaxNumberReasonCategory | '' {
  if (!isNoTaxNumberReasonCode(code)) {
    return '';
  }

  return NO_TAX_NUMBER_REASON_CODE_TO_CATEGORY[code];
}

export function getDefaultNoTaxNumberReasonCodeForCategory(
  category: NoTaxNumberReasonCategory,
): NoTaxNumberReasonCode | '' {
  if (category === NoTaxNumberReasonCategory.COUNTRY_DOES_NOT_ISSUE_TIN) {
    return NoTaxNumberReasonCode.COUNTRY_DOES_NOT_ISSUE_TIN;
  }

  return '';
}

export function shouldShowNoTaxNumberReasonCodeDropdown(
  category?: NoTaxNumberReasonCategory | '',
): boolean {
  return (
    category === NoTaxNumberReasonCategory.UNABLE_TO_PROVIDE_NOW ||
    category === NoTaxNumberReasonCategory.NOT_LEGALLY_REQUIRED
  );
}

export function getNoTaxNumberReasonCodesForCategory(
  category: NoTaxNumberReasonCategory,
): readonly NoTaxNumberReasonCode[] {
  if (category === NoTaxNumberReasonCategory.UNABLE_TO_PROVIDE_NOW) {
    return UNABLE_TO_PROVIDE_NOW_REASON_CODES;
  }

  if (category === NoTaxNumberReasonCategory.NOT_LEGALLY_REQUIRED) {
    return NOT_LEGALLY_REQUIRED_REASON_CODES;
  }

  return [];
}

export function resetForeignNoTaxNumberReasonFields(
  resetField: UseFormResetField<OtherCountriesTaxFormValues>,
  index: number,
) {
  resetField(`foreignTaxResidencies.${index}.noTaxNumberReasonCategory`, {
    defaultValue: '',
  });
  resetField(`foreignTaxResidencies.${index}.noTaxNumberReasonCode`, {
    defaultValue: '',
  });
  resetField(`foreignTaxResidencies.${index}.noTaxNumberReasonOther`, {
    defaultValue: '',
  });
}

export function getForeignNoTaxNumberReasonGroupedSelectConfig(
  t: TranslatorFunction,
): {
  groups: GroupedSelectGroup[];
  otherOption?: GroupedSelectOption;
} {
  return {
    groups: [
      {
        options: FOREIGN_NO_TAX_NUMBER_REASON_CATEGORY_CODES.map(code => ({
          value: code,
          label: t(
            `taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.options.${code}`,
          ),
        })),
      },
    ],
  };
}

export function getForeignNoTaxNumberReasonCodeGroupedSelectConfig(
  t: TranslatorFunction,
  category: NoTaxNumberReasonCategory,
): {
  groups: GroupedSelectGroup[];
} {
  return {
    groups: [
      {
        options: getNoTaxNumberReasonCodesForCategory(category).map(code => ({
          value: code,
          label: t(
            `taxInfoDrawer.question2.foreignTaxResidency.noTaxNumberReason.codeOptions.${code}`,
          ),
        })),
      },
    ],
  };
}
